import { Injectable } from '@angular/core';
import {
  localDatePlusYearsInput,
  localTodayDateInput,
  toLocalDateInput,
} from '../../utils/date-input.util';
import {
  CardAdvanceActivateCode,
  CardAdvanceActionPayload,
  CardAdvanceDetail,
  CardAdvanceListRow,
  CardAdvanceTableDto,
  FinanceForCardAdvanceSearch,
  SaveCardAdvancePayload,
  StandardAdvanceCardConfig,
  StandardAdvanceConfigOption,
} from '../../models/card-quota/card-advance.model';
import { AdvanceVehicleTypeOption } from '../../models/card-quota/quota-card-advance.model';
import { MapperQuotaCardAdvanceService } from './mapper-quota-card-advance.service';

@Injectable({ providedIn: 'root' })
export class MapperCardAdvanceService {
  constructor(private readonly quotaMapper: MapperQuotaCardAdvanceService) {}

  mapTable(dto: CardAdvanceTableDto): {
    items: CardAdvanceListRow[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } {
    const raw = dto as CardAdvanceTableDto & Record<string, unknown>;
    const rows = (dto.data ?? raw['Data'] ?? []) as CardAdvanceListRow[];
    return {
      items: rows.map((r) => this.mapListRow(r as CardAdvanceListRow & Record<string, unknown>)),
      total: Number(dto.total ?? raw['Total'] ?? 0),
      page: Number(dto.page ?? raw['Page'] ?? 1),
      pageSize: Number(dto.pageSize ?? raw['PageSize'] ?? 20),
      totalPages: Number(dto.totalPages ?? raw['TotalPages'] ?? 0),
    };
  }

  mapListRow(row: CardAdvanceListRow & Record<string, unknown>): CardAdvanceListRow {
    return {
      id: Number(row.id ?? row['Id'] ?? 0),
      name: String(row.name ?? row['Name'] ?? ''),
      clientId: Number(row.clientId ?? row['ClientId'] ?? 0),
      clientName: String(row.clientName ?? row['ClientName'] ?? ''),
      siteId: Number(row.siteId ?? row['SiteId'] ?? 0),
      cardTypeId: Number(row.cardTypeId ?? row['CardTypeId'] ?? 0),
      cardTypeName: String(row.cardTypeName ?? row['CardTypeName'] ?? ''),
      vehicleTypeId: Number(row.vehicleTypeId ?? row['VehicleTypeId'] ?? 0),
      vehicleTypeName: String(row.vehicleTypeName ?? row['VehicleTypeName'] ?? ''),
      qty: Number(row.qty ?? row['Qty'] ?? 0),
      status: Number(row.status ?? row['Status'] ?? 0),
      financeId: row.financeId != null ? Number(row.financeId ?? row['FinanceId']) : null,
      financeReceiptId:
        row.financeReceiptId != null ? String(row.financeReceiptId ?? row['FinanceReceiptId']) : null,
      startDate: row.startDate != null ? toLocalDateInput(String(row.startDate ?? row['StartDate'])) : null,
      endDate: row.endDate != null ? toLocalDateInput(String(row.endDate ?? row['EndDate'])) : null,
      createDate: row.createDate != null ? String(row.createDate ?? row['CreateDate']) : null,
      activateCodeCount: Number(row.activateCodeCount ?? row['ActivateCodeCount'] ?? 0),
      advanceKind: String(row.advanceKind ?? row['AdvanceKind'] ?? 'admin'),
      activateExpireDays: Number(row.activateExpireDays ?? row['ActivateExpireDays'] ?? 10),
    };
  }

  mapDetail(dto: CardAdvanceDetail & Record<string, unknown>): CardAdvanceDetail {
    const base = this.mapListRow(dto);
    return {
      ...base,
      subscriptionId: Number(dto.subscriptionId ?? dto['SubscriptionId'] ?? 0),
      subscriptionName: String(dto.subscriptionName ?? dto['SubscriptionName'] ?? ''),
      cardScope: String(dto.cardScope ?? dto['CardScope'] ?? ''),
      remark: dto.remark != null ? String(dto.remark ?? dto['Remark']) : null,
    };
  }

  mapVehicleTypes(rows: AdvanceVehicleTypeOption[] | Record<string, unknown>[]): AdvanceVehicleTypeOption[] {
    return this.quotaMapper.mapVehicleTypes(rows);
  }

  mapCardConfig(row: StandardAdvanceCardConfig & Record<string, unknown>): StandardAdvanceCardConfig {
    return {
      cardTypeId: Number(row.cardTypeId ?? row['CardTypeId'] ?? 0),
      cardTypeName: String(row.cardTypeName ?? row['CardTypeName'] ?? ''),
      subscriptionId: Number(row.subscriptionId ?? row['SubscriptionId'] ?? 0),
      subscriptionName: String(row.subscriptionName ?? row['SubscriptionName'] ?? ''),
      vehicleTypeId: Number(row.vehicleTypeId ?? row['VehicleTypeId'] ?? 0),
      vehicleTypeName: String(row.vehicleTypeName ?? row['VehicleTypeName'] ?? ''),
      cardScope: String(row.cardScope ?? row['CardScope'] ?? ''),
    };
  }

  mapConfigOptions(
    rows: StandardAdvanceConfigOption[] | Record<string, unknown>[]
  ): StandardAdvanceConfigOption[] {
    return (rows ?? []).map((row) => {
      const r = row as StandardAdvanceConfigOption & Record<string, unknown>;
      return {
        configId: Number(r.configId ?? r['ConfigId'] ?? 0),
        cardTypeId: Number(r.cardTypeId ?? r['CardTypeId'] ?? 0),
        cardTypeName: String(r.cardTypeName ?? r['CardTypeName'] ?? ''),
        subscriptionId: Number(r.subscriptionId ?? r['SubscriptionId'] ?? 0),
        subscriptionName: String(r.subscriptionName ?? r['SubscriptionName'] ?? ''),
        vehicleTypeId: Number(r.vehicleTypeId ?? r['VehicleTypeId'] ?? 0),
        vehicleTypeName: String(r.vehicleTypeName ?? r['VehicleTypeName'] ?? ''),
        cardScope: String(r.cardScope ?? r['CardScope'] ?? ''),
      };
    });
  }

  cardScopeLabel(scope: string): string {
    switch ((scope ?? '').toLowerCase()) {
      case 'free':
        return 'บัตรฟรี';
      case 'expiry':
        return 'บัตรมีหมดอายุ';
      case 'both':
        return 'ฟรี + หมดอายุ';
      default:
        return scope || '-';
    }
  }

  mapFinanceSearch(rows: FinanceForCardAdvanceSearch[] | Record<string, unknown>[]): FinanceForCardAdvanceSearch[] {
    return (rows ?? []).map((row) => {
      const r = row as FinanceForCardAdvanceSearch & Record<string, unknown>;
      return {
        id: Number(r.id ?? r['Id'] ?? 0),
        receiptId: String(r.receiptId ?? r['ReceiptId'] ?? ''),
        paidDate: r.paidDate != null ? String(r.paidDate ?? r['PaidDate']) : null,
        fee: Number(r.fee ?? r['Fee'] ?? 0),
        vat: Number(r.vat ?? r['Vat'] ?? 0),
        total: Number(r.total ?? r['Total'] ?? 0),
      };
    });
  }

  mapActivateCodes(rows: CardAdvanceActivateCode[] | Record<string, unknown>[]): CardAdvanceActivateCode[] {
    return this.quotaMapper.mapActivateCodes(rows);
  }

  statusLabel = (status: number) => this.quotaMapper.statusLabel(status);

  createEmptyForm(siteId: number): CardAdvanceDetail {
    return {
      id: 0,
      name: '',
      clientId: 0,
      clientName: '',
      siteId,
      cardTypeId: 0,
      cardTypeName: '',
      vehicleTypeId: 0,
      vehicleTypeName: '',
      qty: 1,
      status: 1,
      financeId: null,
      financeReceiptId: null,
      startDate: localTodayDateInput(),
      endDate: localDatePlusYearsInput(1),
      createDate: null,
      subscriptionId: 0,
      subscriptionName: '',
      cardScope: '',
      remark: null,
      activateCodeCount: 0,
      advanceKind: 'admin',
      activateExpireDays: 10,
    };
  }

  buildSavePayload(
    form: CardAdvanceDetail,
    userId: number,
    menuId: number,
    submitForApproval: boolean,
    financeId: number | null
  ): SaveCardAdvancePayload {
    return {
      userId,
      menuId,
      id: form.id > 0 ? form.id : null,
      clientId: form.clientId,
      siteId: form.siteId,
      vehicleTypeId: form.vehicleTypeId,
      subscriptionId: form.subscriptionId,
      qty: form.qty,
      startDate: form.startDate ?? '',
      endDate: form.endDate ?? '',
      remark: form.remark,
      financeId: financeId && financeId > 0 ? financeId : null,
      submitForApproval,
      activateExpireDays: form.activateExpireDays > 0 ? form.activateExpireDays : 10,
    };
  }

  buildActionPayload(id: number, userId: number, menuId: number): CardAdvanceActionPayload {
    return { id, userId, menuId };
  }
}
